import { type Edge as ReactFlowEdge } from '@xyflow/react'

import { type CustomNode } from './types'

/**
 * Builds a map of parent node IDs to their children node IDs from edges
 */
export const buildChildrenMap = (edges: ReactFlowEdge[]): Map<string, string[]> => {
  const childrenMap = new Map<string, string[]>()

  edges.forEach((edge) => {
    const children = childrenMap.get(edge.source) || []
    children.push(edge.target)
    childrenMap.set(edge.source, children)
  })

  return childrenMap
}

/**
 * Builds a map of child node IDs to their parent node ID from edges
 */
export const buildParentMap = (edges: ReactFlowEdge[]): Map<string, string> => {
  const parentMap = new Map<string, string>()

  edges.forEach((edge) => {
    parentMap.set(edge.target, edge.source)
  })

  return parentMap
}

/**
 * Builds a map of node IDs to their node objects for efficient lookups
 */
export const buildNodeMap = (nodes: CustomNode[]): Map<string, CustomNode> => {
  const nodeMap = new Map<string, CustomNode>()

  nodes.forEach((node) => {
    nodeMap.set(node.id, node)
  })

  return nodeMap
}

/**
 * Finds all descendants of a node using BFS traversal
 */
export const getDescendants = (nodeId: string, childrenMap: Map<string, string[]>): string[] => {
  const descendants: string[] = []
  const queue = [nodeId]

  while (queue.length > 0) {
    const current = queue.shift()
    if (!current) continue

    const children = childrenMap.get(current) || []
    descendants.push(...children)
    queue.push(...children)
  }

  return descendants
}

/**
 * Finds all ancestors of a node by traversing up the tree
 */
export const getAncestors = (nodeId: string, parentMap: Map<string, string>): string[] => {
  const ancestors: string[] = []
  let currentId: string | undefined = nodeId

  while (currentId) {
    const parentId = parentMap.get(currentId)
    if (parentId) {
      ancestors.push(parentId)
      currentId = parentId
    } else {
      break
    }
  }

  return ancestors
}
